import axios from 'axios';
import {
  DeleteGroupParams,
  EditGroupSettingsParams,
  GROUP_SETTINGS_INITIAL_STATE,
  GroupSettingsSet
} from './group-settings.model';
import { CallStateEnum } from '@shared/models/call-state.model';
import { SnackbarType } from '@shared/models/app-snackbar.model';
import useSnackbarStore from '@store/snackbar-store/snackbar.store';
import { getDeviceScreensList } from '@api/digital-signage/screens-api.ts';
import { getPlaylistsList } from '@api/digital-signage/playlists-api.ts';
import {
  createDeviceGroup,
  deleteDeviceGroup,
  editDeviceGroup as editDeviceGroupApi,
  getDeviceGroupById,
  getDeviceGroupsList
} from '@api/digital-signage/device-groups-api.ts';
import {
  getSignageDevicesList,
  reloadDevices
} from '@api/digital-signage/devices-api.ts';
import { DeviceLocationEnum } from '@modules/digital-signage/model/devices.model.ts';

const { showSnackbar } = useSnackbarStore.getState();

export const groupSettingsSlice = (set: GroupSettingsSet) => ({
  ...GROUP_SETTINGS_INITIAL_STATE,

  getScreensOptions: async () => {
    set({ screensCallState: CallStateEnum.Loading });
    try {
      const screensList = await getDeviceScreensList();

      set({
        screensList,
        screensOptions: screensList.map((item) => ({
          id: item._id,
          title: item.title,
          value: item.title
        })),
        screensCallState: CallStateEnum.Loaded
      });
    } catch (error) {
      set({ screensCallState: CallStateEnum.Error });
      if (axios.isAxiosError(error)) {
        showSnackbar(error.response?.data?.message, SnackbarType.Error);
      }
    }
  },

  getPlaylistsOptions: async () => {
    set({ playlistsCallState: CallStateEnum.Loading });
    try {
      const playlistsList = await getPlaylistsList();

      set({
        playlistsList,
        playlistsOptions: playlistsList.map((item) => ({
          id: item._id,
          title: item.name,
          value: item.name
        })),
        playlistsCallState: CallStateEnum.Loaded
      });
    } catch (error) {
      set({ playlistsCallState: CallStateEnum.Error });
      if (axios.isAxiosError(error)) {
        showSnackbar(error.response?.data?.message, SnackbarType.Error);
      }
    }
  },

  getGroupDetails: async (id: string) => {
    set({ groupDetailsCallState: CallStateEnum.Loading });
    try {
      const groupDetails = await getDeviceGroupById(id);
      set({ groupDetails, groupDetailsCallState: CallStateEnum.Loaded });
    } catch (error) {
      set({ groupDetailsCallState: CallStateEnum.Error });
      if (axios.isAxiosError(error)) {
        showSnackbar(error.response?.data?.message, SnackbarType.Error);
      }
    }
  },

  getDevicesOptions: async () => {
    set({ devicesCallState: CallStateEnum.Loading });
    try {
      const devicesList = await getSignageDevicesList();

      set({
        devicesList,
        devicesOptions: devicesList.map((item) => ({
          id: item._id,
          title: item.name,
          value: item.name
        })),
        devicesCallState: CallStateEnum.Loaded
      });
    } catch (error) {
      set({ devicesCallState: CallStateEnum.Error });
      if (axios.isAxiosError(error)) {
        showSnackbar(error.response?.data?.message, SnackbarType.Error);
      }
    }
  },

  getDeviceGroupsList: async () => {
    set({ deviceGroupsCallState: CallStateEnum.Loading });
    try {
      const deviceGroupsList = await getDeviceGroupsList();
      set({ deviceGroupsList, deviceGroupsCallState: CallStateEnum.Loaded });
    } catch (error) {
      set({ deviceGroupsCallState: CallStateEnum.Error });
      if (axios.isAxiosError(error)) {
        showSnackbar(error.response?.data?.message, SnackbarType.Error);
      }
    }
  },

  editGroupSettings: async ({
    id,
    body,
    state,
    handleClose,
    onSuccess
  }: EditGroupSettingsParams) => {
    set({ callState: CallStateEnum.Loading });
    try {
      if (id) await editDeviceGroupApi(id, body);
      else await createDeviceGroup(body);
      set({ callState: CallStateEnum.Loaded });
      handleClose?.();
      onSuccess?.();
      state?.setApiFunctionAndFetchData(state.apiFunction);
      showSnackbar(
        'snackbar_success_group_settings_text',
        SnackbarType.Success
      );
    } catch (error) {
      set({ callState: CallStateEnum.Error });
      if (axios.isAxiosError(error)) {
        showSnackbar(error.response?.data?.message, SnackbarType.Error);
      }
    }
  },

  reloadGroupDevices: async (ids: string[]) => {
    if (!ids.length) return;
    try {
      await reloadDevices({ ids, deviceLocation: DeviceLocationEnum.Signage });
      showSnackbar('snackbar_success_device_reload_text', SnackbarType.Success);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        showSnackbar(error.response?.data?.message, SnackbarType.Error);
      }
    }
  },

  deleteGroup: async ({
    id,
    state,
    handleClose,
    onSuccess
  }: DeleteGroupParams) => {
    set({ callState: CallStateEnum.Loading });
    try {
      await deleteDeviceGroup(id);
      set({ callState: CallStateEnum.Loaded });
      handleClose?.();
      onSuccess?.();
      state?.setApiFunctionAndFetchData(state.apiFunction);
      showSnackbar(
        'snackbar_delete_digital_signage_device_group_text',
        SnackbarType.Success
      );
    } catch (error) {
      set({ callState: CallStateEnum.Error });
      if (axios.isAxiosError(error)) {
        showSnackbar(error.response?.data?.message, SnackbarType.Error);
      }
    }
  },

  resetGroupDevices: () =>
    set({
      groupDetails: null,
      groupDetailsCallState: CallStateEnum.Init,
      devicesList: [],
      devicesOptions: [],
      devicesCallState: CallStateEnum.Init
    })
});
